/* eslint-disable react/display-name */
'use client';

import React, { useEffect, useMemo, useState } from 'react';

import cx from 'classnames';
import { map } from 'lodash';

import { DateCellType } from './date.types';
import { DAYS, MONTHS } from './constant';
import Icon, { ICON_TYPES } from '../icons';
import Dropdown from '../Dropdown/Dropdown';
import { getDatesToRender, isTwoDatesEqual } from './helper';

import styles from './calendar.module.scss';

interface Props {
  startDate?: Date;
  endDate?: Date;
  handleDateChange: (date: DateCellType, index?: number) => void;
  className?: string;
  leftArrowIcon?: string | React.ReactNode;
  rightArrowIcon?: string | React.ReactNode;
  dayClassName?: string;
  daysContianerClassName?: string;
  dateClassName?: string;
  selectedDateClassName?: string;
  disabledDateClassName?: string;
  currentDateClassName?: string;
  startDateIndex?: number;
}

const Calendar = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    startDate,
    endDate,
    handleDateChange,
    className,
    leftArrowIcon = ICON_TYPES.LEFTARROW,
    rightArrowIcon = ICON_TYPES.RIGHTARROW,
    dayClassName,
    daysContianerClassName,
    dateClassName,
    selectedDateClassName,
    disabledDateClassName,
    currentDateClassName,
  } = props;

  //* date for internal user
  const [dateForInternalUser, setDateForInternalUser] = useState(new Date());

  const daysArray = useMemo(() => getDatesToRender(dateForInternalUser), [dateForInternalUser]);

  const leftContainerRefs = useMemo(
    () => map(Array(daysArray.length).fill(0), () => React.createRef<HTMLDivElement>()),
    [daysArray.length],
  );

  const rightContainerRefs = useMemo(
    () => map(Array(daysArray.length).fill(0), () => React.createRef<HTMLDivElement>()),
    [daysArray.length],
  );

  const handlePrevMonth = () => {
    setDateForInternalUser(
      new Date(dateForInternalUser.getFullYear(), dateForInternalUser.getMonth() - 1),
    );
  };

  const handleNextMonth = () => {
    setDateForInternalUser(
      new Date(dateForInternalUser.getFullYear(), dateForInternalUser.getMonth() + 1),
    );
  };

  const isDateSelected = (dateObj: DateCellType) => {
    if (startDate && isTwoDatesEqual(startDate, new Date(dateObj.timeStamp))) return true;
    if (endDate && isTwoDatesEqual(endDate, new Date(dateObj.timeStamp))) return true;
    return false;
  };

  const isLeftPartInRange = (dateObj: DateCellType) => {
    if (!startDate || !endDate || dateObj.isDisabled) return false;
    if (dateObj.timeStamp > startDate.getTime() && dateObj.timeStamp <= endDate.getTime())
      return true;
    return false;
  };

  const isRightPartInRange = (dateObj: DateCellType) => {
    if (!startDate || !endDate || dateObj.isDisabled) return false;
    if (dateObj.timeStamp >= startDate.getTime() && dateObj.timeStamp < endDate.getTime())
      return true;
    return false;
  };

  useEffect(() => {
    if (startDate) {
      setDateForInternalUser(new Date(startDate.getFullYear(), startDate.getMonth()));
    }
  }, [startDate]);

  return (
    <div className={cx(styles.container, className)} ref={ref} onClick={(e) => e.stopPropagation()}>
      <div className={styles.headerContianer}>
        <div className={cx(styles.iconContainer)} onClick={handlePrevMonth}>
          <Icon icon={leftArrowIcon} />
        </div>
        <div className={styles.monthYearContianer}>
          <Dropdown
            options={MONTHS}
            placeholder={'Months'}
            width="116px"
            size="small"
            suffixIcon={ICON_TYPES.DOWN}
            onMenuItemClick={(item) =>
              setDateForInternalUser(
                new Date(dateForInternalUser.getFullYear(), Number(item.value)),
              )
            }
            value={MONTHS[dateForInternalUser.getMonth()]}
            className={styles.monthDropdown}
          />
          <Dropdown
            options={map(Array(101), (item, index) => ({
              label: `${index + 1970}`,
              value: `${index + 1970}`,
            }))}
            placeholder={'Years'}
            width="116px"
            size="small"
            suffixIcon={ICON_TYPES.DOWN}
            onMenuItemClick={(item) =>
              setDateForInternalUser(new Date(Number(item.label), dateForInternalUser.getMonth()))
            }
            value={{
              label: `${dateForInternalUser.getFullYear()}`,
              value: `${dateForInternalUser.getFullYear()}`,
            }}
            className={styles.yearDropdown}
          />
        </div>
        <div
          className={cx(styles.rightIconContianer, styles.iconContainer)}
          onClick={handleNextMonth}
        >
          <Icon icon={rightArrowIcon} />
        </div>
      </div>
      <div className={cx(styles.daysContianer, daysContianerClassName)}>
        {map(DAYS, (day) => (
          <div className={cx(styles.day, dayClassName)} key={day}>
            {day}
          </div>
        ))}
      </div>
      <div className={cx(styles.daysContianer, daysContianerClassName)}>
        {map(daysArray, (day, index) => (
          <div className={cx(styles.dateCellContainer)} key={day.date + index + day.timeStamp}>
            <div
              ref={leftContainerRefs[index]}
              className={cx(styles.leftPart, { [styles.dateInRange]: isLeftPartInRange(day) })}
            />
            <div
              ref={rightContainerRefs[index]}
              className={cx(styles.rightPart, { [styles.dateInRange]: isRightPartInRange(day) })}
            />
            <div
              className={cx(
                styles.date,
                dateClassName,
                { [cx(styles.selectedDate, selectedDateClassName)]: isDateSelected(day) },
                {
                  [cx(styles.curretDate, currentDateClassName)]: isTwoDatesEqual(
                    new Date(),
                    new Date(day.timeStamp),
                  ),
                },
                { [cx(styles.disabledDay, disabledDateClassName)]: day.isDisabled },
              )}
              onClick={() => {
                if (!day.isDisabled) handleDateChange(day, index);
              }}
            >
              {day.date}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

export default Calendar;

//* add this to the dateCell for hover effect
// onMouseEnter={() => {
//   if (startDate && endDate) return;
//   if (!startDate || !startDateIndex) return;

//   let startDateIndexToUse = startDateIndex;
//   let endDateIndexToUse = index;

//   if (day.timeStamp < startDate.getTime()) {
//     startDateIndexToUse = index;
//     endDateIndexToUse = startDateIndex;
//     // return;
//   }

//   //* add the hover to all divs in between startIndx and index
//   for (let i = startDateIndexToUse; i <= endDateIndexToUse; i++) {
//     const leftDivRef = leftContainerRefs[i].current;
//     const rightDivRef = rightContainerRefs[i].current;
//     if (!leftDivRef || !rightDivRef || daysArray[i].isDisabled) return;
//     if (i !== startDateIndexToUse) {
//       leftDivRef.classList.add(styles.dateCellHover);
//     }
//     if (i !== endDateIndexToUse) {
//       rightDivRef.classList.add(styles.dateCellHover);
//     }
//   }

//   //* remove the hover from all divs in between startIndx and index
//   for (let i = endDateIndexToUse; i < daysArray.length; i++) {
//     const leftDivRef = leftContainerRefs[i].current;
//     const rightDivRef = rightContainerRefs[i].current;
//     if (!leftDivRef || !rightDivRef) return;

//     if (i !== endDateIndexToUse) {
//       leftDivRef.classList.remove(styles.dateCellHover);
//     }
//     rightDivRef.classList.remove(styles.dateCellHover);
//   }

//   for (let i = 0; i < startDateIndexToUse; i++) {
//     const leftDivRef = leftContainerRefs[i].current;
//     const rightDivRef = rightContainerRefs[i].current;
//     if (!leftDivRef || !rightDivRef) return;

//     if (i !== startDateIndexToUse) {
//       leftDivRef.classList.remove(styles.dateCellHover);
//     }
//     rightDivRef.classList.remove(styles.dateCellHover);
//   }
// }}
